/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useCallback, useEffect, useState, lazy, useRef} from 'react'
import {Redirect} from 'react-router'
import {useHistory, useLocation, useParams, Link as RouteLink} from 'react-router-dom'

import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

// Components
import {Box, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    useProduct,
    useProducts,
    useCategory,
    useShopperBasketsMutation,
    useShopperBasketsMutationHelper,
    usePage
} from '@salesforce/commerce-sdk-react'
// Project Components
import ProductView from '../../components/product-view'
import InformationAccordion from './partials/information-accordion'
import SEOStructuredData from '../../components/seo-structured-data'

import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

// Constants
import {
    API_ERROR_MESSAGE,
    MAX_CACHE_AGE,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ALREADY_IN_WISHLIST,
    STALE_WHILE_REVALIDATE
} from '@salesforce/retail-react-app/app/constants'
import {
    BASKET_ITEM_ADD_ERROR,
    CART_ERROR_PRODUCT_NOT_AVAILABLE_QTY,
    EINSTEIN_RECOMMENDERS,
    PAGE_NOT_FOUND_PDC_PAGE_ID
} from '../../constants'
import {COLOR_ID_SEPARATOR} from '../../util/constants'

// OOTB Utils
import {
    normalizeSetBundleProduct,
    getUpdateBundleChildArray
} from '@salesforce/retail-react-app/app/utils/product-utils'
import {rebuildPathWithParams} from '@salesforce/retail-react-app/app/utils/url'

// OOTB Hooks
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import RedirectWithStatus from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/redirect-with-status'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {useVariant} from '@salesforce/retail-react-app/app/hooks'

// Custom Hooks
import useShipToCountry from '../../hooks/use-ship-to-country'
import {useToast} from '@app/hooks/use-toast'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import useEinstein from '../../hooks/use-einstein'
import useMultiSite from '@app/hooks/use-multi-site'
import useProductUrlLifeCycle from '../../hooks/use-product-url-life-cycle'
import {useGTM} from '@app/hooks/use-gtm'
import {useWishlistActions} from '@app/hooks/use-wishlist-actions'
import useSiteType from '@app/hooks/use-site-type'

// Custom Components
import AdditionalBlock from '../../components/product-view/additionalBlock'
import SeoPDPLinks from '../../components/product-view/seoPDPLinks'
import SeoLinks from '../../components/seo-links'

// Utils
import {getProductType} from '@app/utils/product-utils'
import {categoryUrlBuilder, productSetURLBuilder} from '../../utils/seo-utils'
import {getPageDesignerRegion} from '../../utils/utils'
// Custom utils
import {buildMetaData} from '../../components/metatags/util/metaDataUtils'

// Custom Components
import MetaTagsComponent from '../../components/metatags'
import {usePageParameters} from '@app/hooks/use-page-parameters'

// Custom Context
import {ProductProvider} from '../../custom-contexts'
import {PDPPersonalizePush} from '../../components/product-view/personalizePush'
import {HideOnDesktop} from '@salesforce/retail-react-app/app/components/responsive'
import {Component, PageContext} from '@salesforce/commerce-sdk-react/components'
import {PDCCollaborationPush} from '../../page-designer/layouts'
import {PDCCollaborationPushTile, PDCLink} from '../../page-designer/assets'

const RecommendedProducts = lazy(() => import('../../components/recommended-products'))

const ProductDetail = () => {
    const {formatMessage} = useIntl()
    const history = useHistory()
    const einstein = useEinstein()
    const activeData = useActiveData()
    const toast = useToast()
    const location = useLocation()
    const {buildUrl, buildURLWithUTM} = useMultiSite()
    const {
        usePageLoadAndCookies,
        triggerViewItemEvent,
        triggerAddToCartEvent,
        isPageViewTriggered
    } = useGTM()

    /****************************** Basket *********************************/
    const {shipToCountry} = useShipToCountry()
    const {data: basket, isLoading: isBasketLoading, refetch} = useCurrentBasket()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const {res} = useServerContext()
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    /*************************** Product Detail and Category ********************/
    const {productId, colorId} = useParams()
    const urlParams = new URLSearchParams(location.search)

    /**
     * Determines the product ID to be used for API calls based on URL parameters and existing product information.
     *
     * The function checks for the presence of a 'pid' parameter in the URL. If it exists, it uses this value
     * (converted to uppercase) as the product ID for API calls. If the 'pid' parameter is not present, it checks
     * for the presence of both a `productId` and a `color` parameter. If both are available, it constructs the
     * product ID by combining the base product ID (part before the hyphen) with the color ID, separated by a
     * defined separator, and converts the result to uppercase. If neither condition is met, it defaults to using
     * the `productId` converted to uppercase.
     *
     * @param {URLSearchParams} urlParams - The URLSearchParams object containing query parameters from the URL.
     * @param {string} productId - The product ID extracted from the URL path or other source.
     * @param {string} COLOR_ID_SEPARATOR - The separator used to concatenate the base product ID and color ID.
     * @returns {string} The product ID to be used for API calls, formatted in uppercase.
     */
    let productIdToBeUsedForAPICalls
    const color = urlParams.get('color') || colorId
    if (urlParams.get('pid')) {
        productIdToBeUsedForAPICalls = urlParams.get('pid').toUpperCase()
    } else if (productId && color) {
        const baseProductId = productId.split('-')[0]
        productIdToBeUsedForAPICalls = `${baseProductId}${COLOR_ID_SEPARATOR}${color}`.toUpperCase()
    } else {
        productIdToBeUsedForAPICalls = productId.toUpperCase()
    }

    const {
        data: product,
        isLoading: isProductLoading,
        isError: isProductError,
        error: productError,
        refetch: refetchProduct
    } = useProduct(
        {
            parameters: {
                id: productIdToBeUsedForAPICalls,
                perPricebook: true,
                expand: [
                    'availability',
                    'promotions',
                    'images',
                    'prices',
                    'variations',
                    'page_meta_tags'
                ],
                allImages: true,
                c_country: shipToCountry?.id
            }
        },
        {
            // When shoppers select a different variant (and the app fetches the new data),
            // the old data is still rendered (and not the skeletons).
            keepPreviousData: true
        }
    )

    const {data: urlLifeCycleData, isLoading: isUrlLifeCycleDataLoading} = useProductUrlLifeCycle(
        productIdToBeUsedForAPICalls
    )

    // Note: Since category needs id from product detail, it can't be server side rendered atm
    // until we can do dependent query on server
    const {
        data: category,
        isError: isCategoryError,
        error: categoryError
    } = useCategory({
        parameters: {
            id: product?.c_masterPrimaryCategoryID || product?.primaryCategoryId,
            levels: 1
        }
    })

    const PAGEDESIGNER_TO_COMPONENT = {
        'commerce_assets.pdcDisplayConfigLink': PDCLink,
        'commerce_assets.pdcPdpCollaborationPushTile': PDCCollaborationPushTile,
        'commerce_layouts.pdcPdpPersonalizePush': PDPPersonalizePush,
        'commerce_layouts.pdcPdpCollaborationPush': PDCCollaborationPush
    }
    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})
    const {isOutlet} = useSiteType()
    const parameters = usePageParameters('pdp-data' + (isOutlet ? '-outlet' : ''), {
        includeCountry: true
    })
    const {data: pdpData} = usePage({parameters})

    /***************************** Collaboration Push ********************************/
    const collaborationPushComponent = getPageDesignerRegion(pdpData, 'main').components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcPdpCollaborationPush'
    )

    /****************************** Personalize Push *********************************/
    const personalizePushComponent = getPageDesignerRegion(pdpData, 'main').components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcPdpPersonalizePush'
    )

    /****************************** Sets and Bundles *********************************/
    const [childProductSelection, setChildProductSelection] = useState({})
    const [childProductOrderability, setChildProductOrderability] = useState({})
    const [selectedBundleQuantity, setSelectedBundleQuantity] = useState(1)
    const childProductRefs = React.useRef({})
    const productType = getProductType(product)

    let bundleChildVariantIds = ''
    if (productType.isBundle)
        bundleChildVariantIds = Object.keys(childProductSelection)
            ?.map((key) => childProductSelection[key].variant.productId)
            .join(',')

    const {data: bundleChildrenData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds,
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory,master))'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true
        }
    )

    if (productType.isBundle && bundleChildrenData) {
        // Loop through the bundle children and update the inventory for variant selection
        product.bundledProducts.forEach(({product: childProduct}, index) => {
            const matchingChildProduct = bundleChildrenData.data.find(
                (bundleChild) => bundleChild.master.masterId === childProduct.id
            )
            if (matchingChildProduct) {
                product.bundledProducts[index].product = {
                    ...childProduct,
                    inventory: matchingChildProduct.inventory
                }
            }
        })
    }

    const comboProduct =
        productType.isSet || productType.isBundle ? normalizeSetBundleProduct(product) : {}

    const [primaryCategory, setPrimaryCategory] = useState(category)
    const variant = useVariant(product)
    // This page uses the `primaryCategoryId` to retrieve the category data. This attribute
    // is only available on `master` products. Since a variation will be loaded once all the
    // attributes are selected (to get the correct inventory values), the category information
    // is overridden. This will allow us to keep the initial category around until a different
    // master product is loaded.
    useEffect(() => {
        if (category) {
            setPrimaryCategory(category)
        }
    }, [category])

    /**************** Product Variant ****************/
    useEffect(() => {
        if (!variant) {
            return
        }
        // update the variation attributes parameter on
        // the url accordingly as the variant changes
        const updatedUrl = rebuildPathWithParams(`${location.pathname}${location.search}`, {
            pid: variant?.productId
        })
        history.replace(updatedUrl)
    }, [variant])

    // GTM Integration
    useEffect(() => {
        if (isPageViewTriggered && !isProductLoading) {
            triggerViewItemEvent('view_item', product)
        }
    }, [isPageViewTriggered, product?.id, isProductLoading])

    /**************** Wishlist ****************/
    const {wishlist, isWishlistLoading, addProductToWishlist} = useWishlistActions()

    const handleAddToWishlist = (productItem, variantItem) => {
        const isItemInWishlist = wishlist?.customerProductListItems?.find(
            (i) => i.productId === variantItem?.productId || i.productId === productItem?.id
        )

        if (!isItemInWishlist) {
            addProductToWishlist(product)
        } else {
            toast({
                title: formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
                status: 'info',
                action: (
                    <RouteLink to={buildUrl('/account/wishlist')}>
                        {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                    </RouteLink>
                )
            })
        }
    }

    /**************** Add To Cart ****************/
    const showToast = useToast()
    const showError = async (error) => {
        let toastTitle = formatMessage(API_ERROR_MESSAGE)

        if (error?.statusCode === BASKET_ITEM_ADD_ERROR) {
            const productId = error?.statusDetails?.productId
            const qty = error?.statusDetails?.quantity

            if (productId && qty) {
                toastTitle = formatMessage(CART_ERROR_PRODUCT_NOT_AVAILABLE_QTY, {
                    productId,
                    qty
                })
            }
        }

        showToast({
            title: toastTitle,
            status: 'error'
        })
    }

    /**
     * Retrieves the quantity of a specific product in the basket.
     *
     * @param {Object} basket - The basket object containing product information.
     * @param {String} productId - The ID of the product for which you want to know the quantity.
     * @returns {Number} - The quantity of the product in the basket, or 0 if the product is not found.
     */
    const getProductQuantityInBasket = (basket, productId) => {
        if (!basket || !basket.productItems) {
            return 0
        }

        const productItem = basket.productItems.find((item) => item.productId === productId)
        return productItem ? productItem.quantity : 0
    }

    const quantityOfProductInBasket = getProductQuantityInBasket(basket, product?.id)

    const [errorInventoryMessage, setErrorInventoryMessage] = useState(false)

    const handleAddToCart = async (productSelectionValues) => {
        try {
            const productItems = productSelectionValues.map(({variant, quantity}) => ({
                productId: variant.productId,
                price: variant.price,
                quantity
            }))
            const basketResponse = await addItemToNewOrExistingBasket(productItems)

            // GTM Integration
            const finalProduct = {
                ...productSelectionValues[0]?.product,
                ...productSelectionValues[0]?.variant
            }

            triggerAddToCartEvent(basketResponse?.productItems, finalProduct)

            await refetch()

            einstein.sendAddToCart(productItems)

            // If the items were successfully added, set the return value to be used
            // by the add to cart modal.
            return productSelectionValues
        } catch (error) {
            if (quantityOfProductInBasket >= product.inventory.stockLevel) {
                setErrorInventoryMessage(true)
            } else {
                const errorBody = await error.response.json()
                if (errorBody?.statusCode === BASKET_ITEM_ADD_ERROR) {
                    // Force a refresh of the product to disable add to cart button
                    // if needed (inventory = 0 for example)
                    refetchProduct()
                }
                showError(errorBody)
            }
        }
    }

    useEffect(() => {
        setErrorInventoryMessage(false)
    }, [product?.id])

    /**************** Product Set/Bundles Handlers ****************/
    const handleChildProductValidation = useCallback(() => {
        // Run validation for all child products. This will ensure the error
        // messages are shown.
        Object.values(childProductRefs.current).forEach(({validateOrderability}) => {
            validateOrderability({scrollErrorIntoView: false})
        })

        // Using ot state for which child products are selected, scroll to the first
        // one that isn't selected.
        const selectedProductIds = Object.keys(childProductSelection)
        const firstUnselectedProduct = comboProduct.childProducts.find(
            ({product: childProduct}) => !selectedProductIds.includes(childProduct.id)
        )?.product

        if (firstUnselectedProduct) {
            // Get the reference to the product view and scroll to it.
            const {ref} = childProductRefs.current[firstUnselectedProduct.id]

            if (ref.scrollIntoView) {
                ref.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end'
                })
            }

            return false
        }

        return true
    }, [product, childProductSelection])

    /**************** Product Set Handlers ****************/
    const handleProductSetAddToCart = () => {
        // Get all the selected products, and pass them to the addToCart handler which
        // accepts an array.
        const productSelectionValues = Object.values(childProductSelection)
        return handleAddToCart(productSelectionValues)
    }

    /**************** Product Bundle Handlers ****************/
    // Top level bundle does not have variants
    const handleProductBundleAddToCart = async (variant, selectedQuantity) => {
        try {
            const childProductSelections = Object.values(childProductSelection)

            const productItems = [
                {
                    productId: product.id,
                    price: product.price,
                    quantity: selectedQuantity,
                    // The add item endpoint in the shopper baskets API does not respect variant selections
                    // for bundle children, so we have to make a follow up call to update the basket
                    // with the chosen variant selections
                    bundledProductItems: childProductSelections.map((child) => {
                        return {
                            productId: child.variant.productId,
                            quantity: child.quantity
                        }
                    })
                }
            ]

            const res = await addItemToNewOrExistingBasket(productItems)

            const bundleChildMasterIds = childProductSelections.map((child) => {
                return child.product.id
            })

            // since the returned data includes all products in basket
            // here we compare list of productIds in bundleProductItems of each productItem to filter out the
            // current bundle that was last added into cart
            const currentBundle = res.productItems.find((productItem) => {
                if (!productItem?.bundledProductItems?.length) return
                const bundleChildIds = productItem?.bundledProductItems?.map((item) => {
                    // seek out the bundle child that still uses masterId as product id
                    return item.productId
                })
                return bundleChildIds.every((id) => bundleChildMasterIds.includes(id))
            })

            const itemsToBeUpdated = getUpdateBundleChildArray(
                currentBundle,
                childProductSelections
            )

            if (itemsToBeUpdated.length) {
                // make a follow up call to update child variant selection for product bundle
                // since add item endpoint doesn't currently consider product bundle child variants
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: res.basketId
                    },
                    body: itemsToBeUpdated
                })
            }

            einstein.sendAddToCart(productItems)

            return childProductSelections
        } catch (error) {
            showError(error)
        }
    }

    /**************** Einstein ****************/
    useEffect(() => {
        if (product && product.type.set) {
            einstein.sendViewProduct(product)
            const childrenProducts = product.setProducts
            childrenProducts.map((child) => {
                try {
                    einstein.sendViewProduct(child)
                } catch (err) {
                    logger.error('Einstein sendViewProduct error', {
                        namespace: 'ProductDetail.useEffect',
                        additionalProperties: {error: err, child}
                    })
                }
                activeData.sendViewProduct(category, child, 'detail')
            })
        } else if (product) {
            try {
                einstein.sendViewProduct(product)
            } catch (err) {
                logger.error('Einstein sendViewProduct error', {
                    namespace: 'ProductDetail.useEffect',
                    additionalProperties: {error: err, product}
                })
            }
            activeData.sendViewProduct(category, product, 'detail')
        }
    }, [product])

    /*
     * Metadata constructed in the getProps method and passed to the MetaTags component.
     */
    const finalMetadata = buildMetaData(product, product?.pageMetaTags)

    usePageLoadAndCookies('product')

    const [isVisible, setIsVisible] = useState(false)
    const wrapperRef = useRef()

    /**
     * useEffect hook that sets up an IntersectionObserver to monitor the visibility of a component.
     *
     * This hook observes the element referenced by `wrapperRef` and sets the `isVisible` state to `true`
     * when the element enters the viewport, with a root margin of 150px. Once the element is visible,
     * the observer disconnects to prevent further observations.
     *
     * Dependencies:
     * - `wrapperRef`: A React ref object pointing to the DOM element to be observed.
     *
     * Cleanup:
     * - On component unmount or when dependencies change, the observer will unobserve the element
     *   to prevent memory leaks.
     *
     * @param {React.MutableRefObject} wrapperRef - A reference to the DOM element to be observed.
     * @param {Function} setIsVisible - A state setter function to update the visibility status.
     */
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true)
                    observer.disconnect()
                }
            },
            {
                rootMargin: '150px', // Load once the component is within 150px of the viewport
                threshold: 0.1
            }
        )

        if (wrapperRef.current) {
            observer.observe(wrapperRef.current)
        }

        return () => {
            if (wrapperRef.current) {
                observer.unobserve(wrapperRef.current)
            }
        }
    }, [wrapperRef])

    /* Redirect to the product set page if the product is a set */
    if (isServer && product?.type?.set) {
        return (
            <RedirectWithStatus
                status={301}
                to={buildURLWithUTM(productSetURLBuilder(product?.id))}
            />
        )
    }

    // Build category URL and redirect the user
    if (
        isServer &&
        urlLifeCycleData?.data?.categoryIdToRedirect &&
        urlLifeCycleData?.data?.parentCategoryTree
    ) {
        return (
            <RedirectWithStatus
                status={301}
                to={buildURLWithUTM(categoryUrlBuilder(urlLifeCycleData.data.categoryIdToRedirect))}
            />
        )
    }

    /**************** Error Handling ****************/
    if (
        isProductError &&
        !isUrlLifeCycleDataLoading &&
        !urlLifeCycleData?.data?.categoryIdToRedirect &&
        !urlLifeCycleData?.data?.parentCategoryTree
    ) {
        const errorStatus = productError?.response?.status
        if (errorStatus) {
            return (
                <Redirect
                    to={buildURLWithUTM(
                        `/${PAGE_NOT_FOUND_PDC_PAGE_ID + (isOutlet ? '-outlet' : '')}`
                    )}
                />
            )
        }
    }

    if (
        isCategoryError &&
        !isUrlLifeCycleDataLoading &&
        !urlLifeCycleData?.data?.categoryIdToRedirect &&
        !urlLifeCycleData?.data?.parentCategoryTree
    ) {
        const errorStatus = categoryError?.response?.status
        if (errorStatus) {
            return (
                <Redirect
                    to={buildURLWithUTM(
                        `/${PAGE_NOT_FOUND_PDC_PAGE_ID + (isOutlet ? '-outlet' : '')}`
                    )}
                />
            )
        }
    }

    return (
        <Box
            className="sf-product-detail-page"
            layerStyle="page"
            data-testid="product-details-page"
        >
            <SeoLinks masterProductId={product?.master?.masterId || productId} />
            <MetaTagsComponent metaData={finalMetadata} />

            <SEOStructuredData
                data={{product: product, productID: productId}}
                type={'product'}
            ></SEOStructuredData>

            <Stack spacing={16}>
                {productType.isSet || productType.isBundle ? (
                    <Fragment>
                        <ProductView
                            product={product}
                            category={primaryCategory?.c_extendedParentCategoryTree || []}
                            addToCart={
                                productType.isSet
                                    ? handleProductSetAddToCart
                                    : handleProductBundleAddToCart
                            }
                            addToWishlist={handleAddToWishlist}
                            isProductLoading={isProductLoading}
                            isBasketLoading={isBasketLoading}
                            isWishlistLoading={isWishlistLoading}
                            validateOrderability={handleChildProductValidation}
                            childProductOrderability={childProductOrderability}
                            setChildProductOrderability={setChildProductOrderability}
                            setSelectedBundleQuantity={setSelectedBundleQuantity}
                            personalizePushComponent={personalizePushComponent}
                        />

                        <hr />

                        {/* TODO: consider `childProduct.belongsToSet` */}
                        {
                            // Render the child products
                            comboProduct.childProducts.map(
                                ({product: childProduct, quantity: childQuantity}) => (
                                    <Box key={childProduct.id} data-testid="child-product">
                                        <ProductView
                                            // Do not use an arrow function as we are manipulating the functions scope.
                                            ref={function (ref) {
                                                // Assign the "set" scope of the ref, this is how we access the internal
                                                // validation.
                                                childProductRefs.current[childProduct.id] = {
                                                    ref,
                                                    validateOrderability: this.validateOrderability
                                                }
                                            }}
                                            product={childProduct}
                                            isProductPartOfSet={productType.isSet}
                                            isProductPartOfBundle={productType.isBundle}
                                            childOfBundleQuantity={childQuantity}
                                            selectedBundleParentQuantity={selectedBundleQuantity}
                                            addToCart={
                                                productType.isSet
                                                    ? (variant, quantity) =>
                                                          handleAddToCart([
                                                              {
                                                                  product: childProduct,
                                                                  variant,
                                                                  quantity
                                                              }
                                                          ])
                                                    : null
                                            }
                                            addToWishlist={
                                                productType.isSet ? handleAddToWishlist : null
                                            }
                                            onVariantSelected={(product, variant, quantity) => {
                                                if (quantity) {
                                                    setChildProductSelection((previousState) => ({
                                                        ...previousState,
                                                        [product.id]: {
                                                            product,
                                                            variant,
                                                            quantity: productType.isBundle
                                                                ? childQuantity
                                                                : quantity
                                                        }
                                                    }))
                                                } else {
                                                    const selections = {...childProductSelection}
                                                    delete selections[product.id]
                                                    setChildProductSelection(selections)
                                                }
                                            }}
                                            isProductLoading={isProductLoading}
                                            isBasketLoading={isBasketLoading}
                                            isWishlistLoading={isWishlistLoading}
                                            setChildProductOrderability={
                                                setChildProductOrderability
                                            }
                                            personalizePushComponent={personalizePushComponent}
                                        />
                                        <InformationAccordion product={childProduct} />

                                        <Box display={['none', 'none', 'none', 'block']}>
                                            <hr />
                                        </Box>
                                    </Box>
                                )
                            )
                        }
                    </Fragment>
                ) : (
                    <Fragment>
                        <ProductView
                            setErrorInventoryMessage={setErrorInventoryMessage}
                            errorInventoryMessage={errorInventoryMessage}
                            product={product}
                            category={primaryCategory?.c_extendedParentCategoryTree || []}
                            addToCart={(variant, quantity) =>
                                handleAddToCart([{product, variant, quantity}])
                            }
                            addToWishlist={handleAddToWishlist}
                            isProductLoading={isProductLoading}
                            isBasketLoading={isBasketLoading}
                            isWishlistLoading={isWishlistLoading}
                            personalizePushComponent={personalizePushComponent}
                        />
                        <InformationAccordion
                            productDetails={product?.c_composition}
                            shortDescription={product?.shortDescription}
                            longDescription={product?.longDescription}
                            productCare={product?.['c_careInstructions']}
                            product={product}
                        />
                    </Fragment>
                )}

                {personalizePushComponent && (
                    <HideOnDesktop>
                        <PageContext.Provider value={contextValue}>
                            <Component component={personalizePushComponent} />
                        </PageContext.Provider>
                    </HideOnDesktop>
                )}

                {/* Additional Blocks */}
                {/* Custom Context to avoid another API call */}
                <ProductProvider productData={product}>
                    <AdditionalBlock />
                </ProductProvider>
                {/* End Additional Blocks */}

                {/* Product Recommendations */}
                <Stack spacing={16}>
                    <Box ref={wrapperRef}>
                        {isVisible && (
                            <>
                                {!productType.isSet && (
                                    <RecommendedProducts
                                        title={
                                            <FormattedMessage
                                                defaultMessage="Complete the Set"
                                                id="product_detail.recommended_products.title.complete_set"
                                            />
                                        }
                                        recommender={EINSTEIN_RECOMMENDERS.PDP_COMPLETE_SET}
                                        products={[product]}
                                        shouldFetch={() => product?.id}
                                        isCompleteTheLook={true}
                                        textTransform={'uppercase'}
                                        nbSlidesToShow={{base: 1, md: 3, lg: 4}}
                                        isScroller={false}
                                        isCarousel={true}
                                        mt={[80, null, null, 120]}
                                    />
                                )}
                                <RecommendedProducts
                                    title={
                                        <FormattedMessage
                                            defaultMessage="Related products"
                                            id="product_detail.recommended_products.title.might_also_like"
                                        />
                                    }
                                    recommender={EINSTEIN_RECOMMENDERS.PDP_MIGHT_ALSO_LIKE}
                                    products={[product]}
                                    shouldFetch={() => product?.id}
                                    isScroller={false}
                                    isCarousel={{base: false, md: true, lg: false}}
                                    maxProducts={4}
                                    mt={[60, null, null, 120]}
                                />
                            </>
                        )}
                    </Box>

                    {collaborationPushComponent && (
                        <Box mt={[40, null, null, 95]}>
                            <PageContext.Provider value={contextValue}>
                                <Component component={collaborationPushComponent} />
                            </PageContext.Provider>
                        </Box>
                    )}

                    <SeoPDPLinks product={product} mt={34} />
                </Stack>
            </Stack>
        </Box>
    )
}

ProductDetail.getTemplateName = () => 'product-detail'

ProductDetail.propTypes = {
    /**
     * The current react router match object. (Provided internally)
     */
    match: PropTypes.object
}

export default ProductDetail
